import {
  getProductQtyStepUnitLabel,
  parsePositiveMoq,
  parsePositiveOqm,
  shouldLockWpaUnitStepOfOne,
  computeLineAmountFromRow,
} from './wpaProductUtils';

const LEGACY_CART_DOCS_KEY = 'wpa_cart_docs';
export const WPA_CART_SESSION_SCOPE_KEY = 'wpa_cart_session_scope';

function nowIso() {
  return new Date().toISOString();
}

function makeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `cart_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const CART_NO_SERIAL_WIDTH = 4;

/** 產生當日購物車單號：yyMMdd + 流水（預設 4 位，與正式 SO 單號格式一致）。 */
function buildCartNo(datePrefix, serial) {
  return `${datePrefix}${String(serial).padStart(CART_NO_SERIAL_WIDTH, '0')}`;
}

/** 當日日期前綴，與 `CommerceOrderNumberService.GenerateNextOrderNoAsync` 保持一致（UTC）。
 *  使用 ISO 字串取 UTC 日期，避免瀏覽器本地時區在 UTC 午夜前後造成差一天。 */
function getTodayCartNoPrefix() {
  const iso = new Date().toISOString(); // e.g. "2026-05-20T16:00:00.000Z"
  return iso.slice(2, 4) + iso.slice(5, 7) + iso.slice(8, 10); // yyMMdd UTC
}

/** 從既有 cartNo 還原日期前綴與流水，舊單號或不符格式者回傳 null。 */
function parseCartNo(cartNo) {
  const s = String(cartNo || '').trim();
  // 只接受「6 碼日期 + N 碼數字」格式；避免 2026xxxx / 20262026xxxx 這類舊資料污染。
  const m = /^(\d{6})(\d+)$/.exec(s);
  if (!m) return null;
  return { datePrefix: m[1], serial: Number(m[2]) };
}

/** 與後端 Guid / 路由參數比對用 */
function normalizeId(v) {
  if (v == null) return '';
  return String(v).replace(/[{}]/g, '').toLowerCase();
}

export function readSessionScope() {
  try {
    const raw = JSON.parse(localStorage.getItem(WPA_CART_SESSION_SCOPE_KEY) || 'null');
    if (!raw || typeof raw !== 'object') return null;
    const companyId = raw.companyId != null ? normalizeId(raw.companyId) : '';
    const identityId = raw.identityId != null ? normalizeId(raw.identityId) : '';
    if (!companyId || !identityId) return null;
    return { companyId, identityId };
  } catch {
    return null;
  }
}

/** 登入成功或 /me 完成後寫入，讓購物車與「公司＋WPA 帳號」綁定 */
export function writeSessionScope(companyId, identityId) {
  const co = normalizeId(companyId);
  const id = normalizeId(identityId);
  if (!co || !id) return;
  localStorage.setItem(WPA_CART_SESSION_SCOPE_KEY, JSON.stringify({ companyId: co, identityId: id }));
}

export function clearSessionScope() {
  localStorage.removeItem(WPA_CART_SESSION_SCOPE_KEY);
}

function getCartDocsStorageKey() {
  const s = readSessionScope();
  if (s) return `wpa_cart_docs__${s.companyId}__${s.identityId}`;
  return LEGACY_CART_DOCS_KEY;
}

function readRawDocs() {
  const key = getCartDocsStorageKey();
  try {
    const raw = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function writeRawDocs(docs) {
  localStorage.setItem(getCartDocsStorageKey(), JSON.stringify(docs));
}

function getCurrentCustomer() {
  try {
    return JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
  } catch {
    return null;
  }
}

function docBelongsToCurrentCustomer(d) {
  const c = getCurrentCustomer();
  const cid = c?.id;
  if (cid == null || cid === '') return false;
  if (d.customerId == null || d.customerId === '') return false;
  return normalizeId(d.customerId) === normalizeId(cid);
}

function joinAddressLines(...parts) {
  return parts
    .filter((p) => p != null && String(p).trim() !== '')
    .map((p) => String(p).trim())
    .join('\n');
}

/** 與後端 /api/wpa-auth/me 一致：優先中文三行，否則英文三行；另有合併欄 deliveryAddressCn / deliveryAddressEn */
function getDefaultAddress(customer) {
  if (!customer) return '';
  const cn = joinAddressLines(
    customer.deliveryAddressCn1,
    customer.deliveryAddressCn2,
    customer.deliveryAddressCn3,
  );
  const en = joinAddressLines(
    customer.deliveryAddressEn1,
    customer.deliveryAddressEn2,
    customer.deliveryAddressEn3,
  );
  return cn || en || customer.deliveryAddressCn || customer.deliveryAddressEn || '';
}

export function listCartDocs() {
  return readRawDocs()
    .filter(docBelongsToCurrentCustomer)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export function getCartDoc(cartId) {
  const d = readRawDocs().find((x) => x.id === cartId) || null;
  if (!d || !docBelongsToCurrentCustomer(d)) return null;
  return d;
}

export function createCartDoc() {
  const docs = readRawDocs();
  const customer = getCurrentCustomer();
  if (!customer?.id) return null;

  const today = getTodayCartNoPrefix();
  // 只在「當日日期前綴」內遞增，避免把舊單號當前綴累積造成雪球 bug。
  const maxSerialToday = docs.reduce((max, d) => {
    const parsed = parseCartNo(d.cartNo);
    if (!parsed || parsed.datePrefix !== today) return max;
    return Math.max(max, parsed.serial);
  }, 0);

  const doc = {
    id: makeId(),
    cartNo: buildCartNo(today, maxSerialToday + 1),
    createdAt: nowIso(),
    updatedAt: nowIso(),
    customerId: customer.id,
    deliveryDate: '',
    poNumber: '',
    remark: '',
    address: getDefaultAddress(customer),
    lines: [],
  };
  docs.unshift(doc);
  writeRawDocs(docs);
  return doc;
}

export function addProductToCart(product, qty, createNew = false, uiLocale) {
  const customer = getCurrentCustomer();
  if (!customer?.id) return null;

  const docs = readRawDocs();
  const myDocs = docs.filter(docBelongsToCurrentCustomer).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

  let target = null;
  if (!createNew && myDocs.length > 0) {
    target = myDocs[0];
  }
  if (!target) {
    const created = createCartDoc();
    if (!created) return null;
    return addProductToCart(product, qty, false, uiLocale);
  }

  const targetIdx = docs.findIndex((d) => d.id === target.id);
  if (targetIdx < 0) return null;

  const lineIdx = (docs[targetIdx].lines || []).findIndex(
    (l) => (l.productId && l.productId === product.id) || (!l.productId && l.itemNo === product.itemNo)
  );
  const amount = Math.max(1, Number(qty) || 1);
  const moqSnap = parsePositiveMoq(product);
  const oqmSnap = parsePositiveOqm(product);
  const qpu = product.qtyPerUnit ?? product.qty_per_unit;
  const qpuNum = qpu != null && qpu !== '' ? Number(qpu) : null;
  const linePackPatch = {
    ...(qpuNum != null && Number.isFinite(qpuNum) && qpuNum > 0 ? { qtyPerUnit: qpuNum } : {}),
    ...(moqSnap != null ? { minOrderQuantity: moqSnap } : {}),
    ...(oqmSnap != null ? { oqm: oqmSnap } : {}),
  };
  if (lineIdx >= 0) {
    docs[targetIdx].lines[lineIdx].quantity += amount;
    if (moqSnap != null && docs[targetIdx].lines[lineIdx].minOrderQuantity == null) {
      docs[targetIdx].lines[lineIdx].minOrderQuantity = moqSnap;
    }
    if (oqmSnap != null && docs[targetIdx].lines[lineIdx].oqm == null) {
      docs[targetIdx].lines[lineIdx].oqm = oqmSnap;
    }
    if (linePackPatch.qtyPerUnit != null && docs[targetIdx].lines[lineIdx].qtyPerUnit == null) {
      docs[targetIdx].lines[lineIdx].qtyPerUnit = linePackPatch.qtyPerUnit;
    }
  } else {
    docs[targetIdx].lines.push({
      id: makeId(),
      productId: product.id || null,
      itemNo: product.itemNo || '',
      name: (product.displayName != null && String(product.displayName).trim()) || product.name || '',
      price: Number(product.price || 0),
      unit: getProductQtyStepUnitLabel(product, uiLocale, product.unit || '') || null,
      unitOfMeasure: product.unitOfMeasure ?? product.unit_of_measure ?? null,
      unitOfBrokenPack: product.unitOfBrokenPack ?? product.unit_of_brokenpack ?? null,
      unitOfMeasureLocales: product.unitOfMeasureLocales ?? product.unit_of_measure_locales ?? null,
      unitOfBrokenPackLocales: product.unitOfBrokenPackLocales ?? product.unit_of_brokenpack_locales ?? null,
      quantity: amount,
      imageUrl: product.imageUrl || null,
      ...linePackPatch,
    });
  }
  docs[targetIdx].updatedAt = nowIso();
  writeRawDocs(docs);
  return docs[targetIdx];
}

export function updateCartDoc(cartId, patch) {
  const docs = readRawDocs();
  const idx = docs.findIndex((d) => d.id === cartId);
  if (idx < 0 || !docBelongsToCurrentCustomer(docs[idx])) return null;
  docs[idx] = { ...docs[idx], ...patch, updatedAt: nowIso() };
  writeRawDocs(docs);
  return docs[idx];
}

export function updateCartLineQty(cartId, lineId, delta) {
  const docs = readRawDocs();
  const idx = docs.findIndex((d) => d.id === cartId);
  if (idx < 0 || !docBelongsToCurrentCustomer(docs[idx])) return null;
  const lines = docs[idx].lines || [];
  const lidx = lines.findIndex((l) => l.id === lineId);
  if (lidx < 0) return null;
  const line = lines[lidx];
  const cur = Number(line.quantity || 1);
  if (delta === 1 || delta === -1) {
    if (shouldLockWpaUnitStepOfOne(line)) {
      docs[idx].updatedAt = nowIso();
      writeRawDocs(docs);
      return docs[idx];
    }
  }
  line.quantity = Math.max(1, cur + delta);
  docs[idx].updatedAt = nowIso();
  writeRawDocs(docs);
  return docs[idx];
}

export function removeCartLine(cartId, lineId) {
  const docs = readRawDocs();
  const idx = docs.findIndex((d) => d.id === cartId);
  if (idx < 0 || !docBelongsToCurrentCustomer(docs[idx])) return null;
  docs[idx].lines = (docs[idx].lines || []).filter((l) => l.id !== lineId);
  docs[idx].updatedAt = nowIso();
  writeRawDocs(docs);
  return docs[idx];
}

export function deleteCartDoc(cartId) {
  const docs = readRawDocs();
  const idx = docs.findIndex((d) => d.id === cartId);
  if (idx < 0 || !docBelongsToCurrentCustomer(docs[idx])) return;
  writeRawDocs(docs.filter((d) => d.id !== cartId));
}

export function getCartDocAmount(doc) {
  if (!doc?.lines) return 0;
  return doc.lines.reduce((sum, l) => sum + computeLineAmountFromRow(l.quantity, l.price, l), 0);
}

export function getCartDocsTotalQty() {
  return readRawDocs().reduce((sum, d) => {
    if (!docBelongsToCurrentCustomer(d)) return sum;
    const lines = d.lines || [];
    return sum + lines.reduce((s, l) => s + Number(l.quantity || 0), 0);
  }, 0);
}

export function getCartDocsCount() {
  return readRawDocs().reduce((count, d) => {
    if (!docBelongsToCurrentCustomer(d)) return count;
    return count + 1;
  }, 0);
}
